import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Gamepad2, Star, Moon, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { BadgeChip } from "../../lib/games";
import { kidApi, errMsg } from "../../lib/api";

export const useGameStatus = () => {
  const [st, setSt] = useState(null);
  const load = useCallback(() => kidApi.get("/kid/games/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return [st, setSt, load];
};

export const GameTimeBadge = ({ left }) => (
  <span data-testid="game-time-badge" className={`zn-chip !min-h-0 ${left !== undefined && left <= 3 ? "bg-[#FF6B4A] text-white" : "bg-[#B8F2C9]"}`}><Gamepad2 size={15} /> {left ?? "…"}m</span>
);

export const GameOver = () => (
  <div data-testid="game-time-up" className="zn-card zn-pop max-w-lg mx-auto p-8 text-center bg-[#7B5CFA] text-white">
    <Moon size={44} className="mx-auto mb-4 zn-wiggle" />
    <h2 className="text-3xl font-bold mb-2">Game time is done for today!</h2>
    <p className="opacity-90 mb-6">Great playing! Why not make a video or draw on real paper?</p>
    <Link to="/kid" data-testid="game-time-up-home" className="zn-btn bg-white text-[#1B1A2E]">Back to Zunii</Link>
  </div>
);

export const GameShell = ({ game, children }) => {
  const [st, setSt] = useGameStatus();
  const [reward, setReward] = useState(null);
  const [round, setRound] = useState(0);
  const Icon = game.icon;

  useEffect(() => {
    const beat = () => document.visibilityState === "visible" && kidApi.post("/kid/games/heartbeat").then((r) => setSt(r.data))
      .catch((e) => e.response?.data?.detail === "game_time_up" && setSt((s) => ({ ...s, game_minutes_left: 0 })));
    const iv = setInterval(beat, 60000);
    return () => clearInterval(iv);
  }, [setSt]);

  const finish = useCallback(async (score, stars) => {
    try {
      const { data } = await kidApi.post("/kid/games/result", { game: game.id, score, stars });
      setReward({ ...data, score, asked: stars });
      setSt((s) => ({ ...s, stars: data.stars, badges: [...(s?.badges || []), ...data.new_badges], daily: data.daily_bonus ? { ...s?.daily, done: true, streak: data.streak } : s?.daily }));
    } catch (e) {
      if (e.response?.data?.detail === "game_time_up") setSt((s) => ({ ...s, game_minutes_left: 0 }));
      else toast.error(errMsg(e));
    }
  }, [game.id, setSt]);

  if (st && st.game_minutes_left === 0) return <GameOver />;

  return (
    <div data-testid={`game-${game.id}`}>
      <div className="flex flex-wrap items-center gap-3 mb-4 sm:mb-6">
        <Link to="/kid/games" data-testid="game-back-btn" className="zn-chip bg-white !px-3"><ArrowLeft size={16} /></Link>
        <span className="w-10 h-10 grid place-items-center rounded-xl border-[3px] border-[#1B1A2E]" style={{ background: game.color }}><Icon size={20} /></span>
        <h1 className="text-2xl sm:text-4xl font-bold flex-1 min-w-0 truncate">{game.title}</h1>
        <div className="w-full sm:w-auto flex gap-2 justify-end -mt-1 sm:mt-0">
          <span className="zn-chip !min-h-0 bg-[#FFD23F]" data-testid="game-stars-total"><Star size={15} fill="#1B1A2E" /> {st?.stars ?? 0}</span>
          <GameTimeBadge left={st?.game_minutes_left} />
        </div>
      </div>
      <div key={round}>{children(finish)}</div>
      <Dialog open={!!reward} onOpenChange={(o) => !o && setReward(null)}>
        <DialogContent data-testid="reward-dialog" className="border-[3px] border-[#1B1A2E] rounded-3xl shadow-[6px_6px_0_0_#1B1A2E] max-w-sm text-center">
          <DialogHeader className="items-center">
            <DialogTitle className="font-display text-3xl">{reward?.stars_earned ? "Awesome!" : "Nice try!"}</DialogTitle>
            <DialogDescription>{reward?.daily_cap_reached ? "You've collected all your stars for today!" : `Score: ${reward?.score}`}</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-2" data-testid="reward-stars">
            {[0, 1, 2].map((i) => <Star key={i} size={44} className={i < (reward?.stars_earned || 0) ? "zn-pop" : "opacity-25"} style={{ animationDelay: `${i * 150}ms` }} fill={i < (reward?.stars_earned || 0) ? "#FFD23F" : "none"} />)}
          </div>
          {reward?.daily_bonus > 0 && (
            <div data-testid="reward-daily-bonus" className="zn-chip mx-auto bg-[#B8F2C9]"><CalendarCheck size={16} /> Daily challenge: +{reward.daily_bonus} bonus stars · {reward.streak}-day streak</div>
          )}
          {reward?.new_badges?.length > 0 && (
            <div data-testid="reward-new-badges">
              <p className="font-display font-semibold mb-2">New badge{reward.new_badges.length > 1 ? "s" : ""}!</p>
              <div className="flex flex-wrap justify-center gap-3">{reward.new_badges.map((b) => <BadgeChip key={b} id={b} />)}</div>
            </div>
          )}
          <div className="flex gap-2">
            <button data-testid="reward-play-again" onClick={() => { setReward(null); setRound((r) => r + 1); }} className="zn-btn bg-[#FF6B4A] text-white flex-1">Play again</button>
            <Link to="/kid/games" data-testid="reward-more-games" className="zn-btn bg-white flex-1">More games</Link>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
